import React, { useEffect, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CloseIcon from '@mui/icons-material/Close';
import './MobileNav.css';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  darkMode: boolean;
  onLoginClick: () => void;
}

const MobileNav: React.FC<MobileNavProps> = memo(({ isOpen, onClose, darkMode, onLoginClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleNav = (path: string) => {
    navigate(path);
    onClose();
  };

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/vacancies', label: 'Browse Vacancies' },
    { path: '/companies', label: 'Companies' },
    ...(isAuthenticated
      ? [
          { path: '/post-vacancy', label: 'Post Vacancy' },
          { path: '/my-referrals', label: 'My Referrals' },
          { path: '/messages', label: 'Messages' },
          { path: '/profile', label: 'Profile' },
        ]
      : []),
  ];

  return (
    <>
      <div className={`mobile-nav-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />
      <nav className={`mobile-nav ${isOpen ? 'open' : ''} ${darkMode ? 'dark' : ''}`}>
        <div className="mobile-nav-header">
          <div className="mobile-nav-logo">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Jobometry</span>
          </div>
          <button className="mobile-nav-close" onClick={onClose} aria-label="Close navigation">
            <CloseIcon />
          </button>
        </div>

        <div className="mobile-nav-content">
          <div className="mobile-nav-section">
            <h3>Navigation</h3>
            <ul className="mobile-nav-list">
              {navItems.map(item => (
                <li key={item.path}>
                  <button
                    className={`mobile-nav-item ${location.pathname === item.path ? 'active' : ''}`}
                    onClick={() => handleNav(item.path)}
                  >
                    <span className="mobile-nav-label">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="mobile-nav-section">
            <h3>Account</h3>
            <div className="mobile-nav-auth">
              {!isAuthenticated ? (
                <>
                  <button className="mobile-auth-btn login" onClick={() => { onLoginClick(); onClose(); }}>Sign In</button>
                  <button className="mobile-auth-btn signup" onClick={() => { onLoginClick(); onClose(); }}>Sign Up</button>
                </>
              ) : (
                <>
                  <div className="mobile-user-info">
                    {user?.avatar ? (
                      <img src={user.avatar} alt="" className="mobile-user-avatar" />
                    ) : (
                      <div className="mobile-user-avatar-placeholder">{user?.name?.charAt(0) || 'U'}</div>
                    )}
                    <span>{user?.name || 'User'}</span>
                  </div>
                  <button className="mobile-auth-btn logout" onClick={() => { logout(); onClose(); }}>Sign Out</button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mobile-nav-footer">
          <p>Global Referral Program by Jobometry</p>
        </div>
      </nav>
    </>
  );
});

MobileNav.displayName = 'MobileNav';
export default MobileNav;
